import React, { useState, useRef, useEffect, useCallback } from "react";
import { CameraOff, PanelLeft, X, ArrowLeft, Camera as CamIcon, Aperture } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { useApp } from "../../context/AppContext";
import { getConversationalResponse } from "../../lib/gemini-ai";
import { useCamera } from "../../hooks/useCamera";
import { useSpeechRecognition } from "../../hooks/useSpeechRecognition";
import { useFaceAnimation } from "../../hooks/useFaceAnimation";
import CortexFace from "../roboface/CortexFace";
import RoboControlBar from "../roboface/RoboControlBar";
import RoboSidePanel from "../roboface/RoboSidePanel";
import { Overline } from "../common/Panel";

export default function RoboFaceCall() {
  const { settings, camImage, sendCamCommand } = useApp();
  const apiKey = settings.geminiKeys || "";

  const [isActive, setIsActive] = useState(false);
  const [storyContent, setStoryContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [lastSpokenTime, setLastSpokenTime] = useState(0);
  const [micEnabled, setMicEnabled] = useState(false);
  const [showEsp32, setShowEsp32] = useState(true);
  const [inlineTab, setInlineTab] = useState("chat");
  const [inlineInput, setInlineInput] = useState("");

  const videoRef = useRef(null);
  const desktopVideoRef = useRef(null);
  const talkingIntervalRef = useRef(null);
  const wakeUpSequenceRef = useRef([]);
  const micEnabledRef = useRef(false);
  const inlineChatRef = useRef(null);
  const ttsSidRef = useRef(0);
  const ttsAudioRef = useRef(null);
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { if (inlineChatRef.current) inlineChatRef.current.scrollTo({ top: inlineChatRef.current.scrollHeight, behavior: "smooth" }); }, [conversationHistory]);

  const handleConversationEnd = useCallback(() => {
    setIsThinking(false);
    if (isGenerating) setIsGenerating(false);
  }, [isGenerating]);

  const handleNewTranscript = (transcript) => {
    handleConversation(transcript);
  };

  const { isCameraOn, error: cameraError, facing, toggleCamera, endCall: endCameraCall, flipCamera } = useCamera(videoRef);

  useEffect(() => {
    if (desktopVideoRef.current && videoRef.current && videoRef.current.srcObject) {
      desktopVideoRef.current.srcObject = videoRef.current.srcObject;
    }
  }, [isCameraOn]);

  const { isListening, transcript, toggleListening, startListening, stopListening } = useSpeechRecognition({
    onTranscript: handleNewTranscript,
    onStart: () => setLastSpokenTime(performance.now()),
    onEnd: () => {},
    onError: (e) => toast.error(`Speech Recognition Error: ${e}`),
  });

  const {
    animationState,
    setAnimationState,
    eyePosition,
    faceBoundingBox,
    isEyeRolling,
    triggerEyeRoll,
    triggerWink,
  } = useFaceAnimation({
    videoRef,
    isCameraOn,
    isThinking,
    isAudioPlaying: !!audioUrl,
    isListening,
    lastSpokenTime,
    apiKey,
  });

  useEffect(() => {
    return () => {
      wakeUpSequenceRef.current.forEach(clearTimeout);
      if (talkingIntervalRef.current) clearInterval(talkingIntervalRef.current);
    };
  }, []);

  const handleMicToggle = useCallback(() => {
    if (micEnabled) {
      setMicEnabled(false);
      micEnabledRef.current = false;
      if (isListening) stopListening();
      setAnimationState("sad");
      setTimeout(() => {
        if (!micEnabledRef.current) setAnimationState("neutral");
      }, 2000);
    } else {
      setMicEnabled(true);
      micEnabledRef.current = true;
      setAnimationState("happy");
      if (!isListening) startListening();
      setTimeout(() => {
        if (micEnabledRef.current) setAnimationState("neutral");
      }, 2000);
    }
  }, [micEnabled, isListening, stopListening, startListening, setAnimationState]);

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = settings.speechRate || 1;
    u.onstart = () => {
      setLastSpokenTime(performance.now());
      if (talkingIntervalRef.current) clearInterval(talkingIntervalRef.current);
      talkingIntervalRef.current = setInterval(() => {
        setAnimationState((prev) => (prev === "mouthOpen" ? "talking" : "mouthOpen"));
      }, 150);
    };
    u.onend = () => {
      if (talkingIntervalRef.current) {
        clearInterval(talkingIntervalRef.current);
        talkingIntervalRef.current = null;
      }
      setAnimationState("neutral");
      handleConversationEnd();
      if (isCameraOn && micEnabledRef.current) {
        setLastSpokenTime(performance.now());
        startListening();
      }
    };
    window.speechSynthesis.speak(u);
  };

  const handleEsp32Commands = useCallback((text) => {
    const lower = text.toLowerCase();
    if (lower.includes("[cam:snap]") || lower.includes("taking a snapshot") || lower.includes("capture") && lower.includes("esp32")) {
      sendCamCommand("snapshot");
      setShowEsp32(true);
      toast.info("ESP32 snapshot requested by AI");
    }
    if (lower.includes("[cam:show]") || lower.includes("showing the esp32") || lower.includes("here's the feed")) {
      setShowEsp32(true);
    }
    if (lower.includes("[cam:hide]") || lower.includes("hiding the esp32")) {
      setShowEsp32(false);
    }
  }, [sendCamCommand]);

  const handleConversation = async (query) => {
    if (!query || isThinking) return;

    setLastSpokenTime(performance.now());
    if (isListening) stopListening();

    setIsThinking(true);
    setAudioUrl(null);
    setConversationHistory((prev) => [...prev, { role: "user", text: query }]);

    const lower = query.toLowerCase();
    if (lower.includes("snap") || lower.includes("capture") || lower.includes("take a photo") || lower.includes("take a picture") || lower.includes("esp32 camera")) {
      sendCamCommand("snapshot");
      setShowEsp32(true);
      toast.info("ESP32 snapshot requested");
    }
    if (lower.includes("show esp") || lower.includes("show camera") || lower.includes("show the feed")) {
      setShowEsp32(true);
    }
    if (lower.includes("hide esp") || lower.includes("hide camera") || lower.includes("hide the feed")) {
      setShowEsp32(false);
    }

    const isAnticipatingLesson = lower.includes("teach") || lower.includes("about");
    if (isAnticipatingLesson) {
      setIsGenerating(true);
      setStoryContent("");
    }

    try {
      if (!apiKey) {
        const fallbackResponse = "Please add your Gemini API key in Settings to enable AI responses.";
        setConversationHistory((prev) => [...prev, { role: "model", text: fallbackResponse }]);
        speak(fallbackResponse);
        return;
      }

      const result = await getConversationalResponse({ query, history: conversationHistory, apiKey });
      const responseText = result.response;
      setConversationHistory((prev) => [...prev, { role: "model", text: responseText }]);
      handleEsp32Commands(responseText);

      if (result.intent === "teach") {
        setStoryContent(responseText);
      } else {
        setStoryContent("");
      }

      speak(responseText);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not get a response.");
      const errMsg = "Sorry, I had trouble thinking of a response.";
      setConversationHistory((prev) => [...prev, { role: "model", text: errMsg }]);
      handleConversationEnd();
    }
  };

  const onCameraOn = async () => {
    wakeUpSequenceRef.current.forEach(clearTimeout);
    wakeUpSequenceRef.current = [];
    setAnimationState("neutral");
    await new Promise((r) => setTimeout(r, 100));
    setAnimationState("blink");
    await new Promise((r) => setTimeout(r, 200));
    setAnimationState("happy");
    await new Promise((r) => setTimeout(r, 1500));
    setAnimationState("neutral");
    setLastSpokenTime(performance.now());
    setMicEnabled(true);
    micEnabledRef.current = true;
    startListening();
  };

  const onCameraOff = () => {
    setMicEnabled(false);
    micEnabledRef.current = false;
    if (isListening) stopListening();
    setAnimationState("sad");
    setTimeout(() => setAnimationState("offline"), 2000);
  };

  const handleWakeUp = () => {
    if (animationState !== "offline" || wakeUpSequenceRef.current.length > 0) return;
    const sequence = [
      () => setAnimationState("shocked"),
      () => {},
      () => setAnimationState("sad"),
      () => setAnimationState("thinking"),
      () => setAnimationState("offline"),
    ];
    const timeouts = [
      setTimeout(sequence[0], 0),
      setTimeout(sequence[1], 500),
      setTimeout(sequence[2], 2000),
      setTimeout(sequence[3], 4000),
      setTimeout(sequence[4], 7000),
    ];
    wakeUpSequenceRef.current = timeouts;
    setTimeout(() => { wakeUpSequenceRef.current = []; }, 7500);
  };

  useEffect(() => {
    if (isThinking) {
      setAnimationState("thinking");
    } else if (animationState === "thinking" && !isThinking) {
      setAnimationState("neutral");
    }
  }, [isThinking, animationState, setAnimationState]);

  const handleToggleCamera = async () => {
    const turningOn = !isCameraOn;
    await toggleCamera();
    if (turningOn) onCameraOn();
    else onCameraOff();
  };

  const handleStartCall = () => {
    setIsActive(true);
    setConversationHistory([]);
    setStoryContent("");
  };

  const handleEndCall = () => {
    endCameraCall();
    setMicEnabled(false);
    micEnabledRef.current = false;
    if (isListening) stopListening();
    window.speechSynthesis?.cancel();
    if (talkingIntervalRef.current) {
      clearInterval(talkingIntervalRef.current);
      talkingIntervalRef.current = null;
    }
    setAnimationState("offline");
    setIsActive(false);
    setConversationHistory([]);
    setStoryContent("");
    setAudioUrl(null);
    setIsThinking(false);
    setIsGenerating(false);
  };

  const videoBorderColor = () => {
    if (!isCameraOn) return "border-transparent";
    switch (animationState) {
      case "happy": case "smile": case "wink":
        return "border-green-500 shadow-green-500/50";
      case "angry":
        return "border-red-500 shadow-red-500/50";
      case "curious": case "thinking":
        return "border-yellow-500 shadow-yellow-500/50";
      case "shocked":
        return "border-purple-500 shadow-purple-500/50";
      default:
        return "border-blue-500 shadow-blue-500/50";
    }
  };

  // ── Landing page idle animation ──
  const [idleAnim, setIdleAnim] = useState("neutral");
  const [idleEye, setIdleEye] = useState({ x: 0, y: 0 });
  const [idleRolling, setIdleRolling] = useState(false);

  useEffect(() => {
    if (isActive) return;
    const anims = ["neutral", "blink", "happy", "neutral", "smile", "blink", "curious", "neutral", "wink", "neutral", "happy", "blink", "thinking", "neutral"];
    let i = 0;
    const animInterval = setInterval(() => {
      setIdleAnim(anims[i % anims.length]);
      i++;
    }, 1500);
    const eyeInterval = setInterval(() => {
      setIdleEye(Math.random() > 0.4
        ? { x: (Math.random() - 0.5) * 1.8, y: (Math.random() - 0.5) * 0.6 }
        : { x: 0, y: 0 }
      );
    }, 2500);
    return () => { clearInterval(animInterval); clearInterval(eyeInterval); };
  }, [isActive]);

  // ── Landing Page ──
  if (!isActive) {
    return (
      <div className="space-y-6">
        <div>
          <Overline>AI Face Call</Overline>
          <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">RoboFace Live</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[var(--surface)] border border-[var(--bd)] rounded-lg p-5 sm:p-8 flex flex-col items-center text-center">
            <div className="w-28 h-28 sm:w-40 sm:h-40 mb-4 sm:mb-6 bg-[var(--surface2)] rounded-xl flex items-center justify-center">
              <CortexFace animation={idleAnim} eyePosition={idleEye} isEyeRolling={idleRolling} />
            </div>
            <h3 className="font-heading font-bold text-lg sm:text-xl mb-2">Meet Cortex</h3>
            <p className="text-[var(--t2)] text-xs sm:text-sm mb-4 sm:mb-6 max-w-sm">
              Your AI-powered robot companion. Cortex can see you through your camera, mirror your expressions,
              and have real conversations - all powered by Gemini AI.
            </p>
            <button
              onClick={handleStartCall}
              className="px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[var(--accent)] text-black font-heading font-bold text-sm active:scale-95 transition-transform"
            >
              Start AI Face Call
            </button>
            {!apiKey && (
              <p className="text-xs text-[var(--warning)] mt-3">
                Add your Gemini API key in Settings for full AI features
              </p>
            )}
          </div>

          <div className="space-y-4">
            {[
              { title: "Expression Mirroring", desc: "Cortex detects your facial expressions via camera and mirrors them in real-time using Gemini Vision." },
              { title: "Voice Conversations", desc: "Speak naturally using your microphone. Cortex listens, thinks, and responds with synthesized speech." },
              { title: "Interactive Teaching", desc: "Ask Cortex to teach you any topic - it generates story-driven explanations on a whiteboard." },
              { title: "Idle Animations", desc: "Cortex has a personality - it blinks, looks around, winks, and even rolls its eyes if you're quiet too long." },
            ].map((f) => (
              <div key={f.title} className="bg-[var(--surface)] border border-[var(--bd)] rounded-lg p-4">
                <h4 className="font-heading font-bold text-sm mb-1">{f.title}</h4>
                <p className="text-[var(--t2)] text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Active Call View ──
  return (
    <div className="flex flex-col" style={{ height: "calc(100dvh - 160px)", minHeight: "400px" }}>
      {/* Header bar */}
      <div className="flex justify-between items-center mb-2 sm:mb-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={handleEndCall} className="w-7 h-7 sm:w-8 sm:h-8 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--accent)] transition-colors">
            <ArrowLeft size={14} />
          </button>
          <h1 className="font-heading text-sm sm:text-lg font-bold tracking-tighter">RoboFace Live</h1>
          {isCameraOn && (
            <span className="hidden sm:flex px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-mono text-[10px] items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" /> CONNECTED
            </span>
          )}
        </div>
        <button onClick={() => setIsSidePanelOpen((p) => !p)} className="hidden md:flex w-8 h-8 rounded-md border border-[var(--bd)] items-center justify-center hover:border-[var(--accent)] transition-colors">
          {isSidePanelOpen ? <X size={16} /> : <PanelLeft size={16} />}
        </button>
      </div>

      {/* ═══ MOBILE LAYOUT (below md) ═══ */}
      <div className="flex-1 flex flex-col gap-2 rounded-lg border border-[var(--bd)] bg-black/90 p-2 overflow-y-auto md:hidden">
        {/* Row 1: Webcam + ESP32 */}
        <div className="grid grid-cols-2 gap-2">
          <div className={cn("relative aspect-square bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center border-2 transition-all", videoBorderColor())}>
            <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover transition-opacity ${isCameraOn ? "opacity-100" : "opacity-0"}`} style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }} />
            {!isCameraOn && (<div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--t2)] z-10"><CameraOff size={24} /><p className="mt-1 text-[9px]">Camera off</p></div>)}
            {faceBoundingBox && videoRef.current && (<div className="absolute border-2 border-green-500/80 rounded-md" style={{ left: `${(faceBoundingBox.x / videoRef.current.videoWidth) * 100}%`, top: `${(faceBoundingBox.y / videoRef.current.videoHeight) * 100}%`, width: `${(faceBoundingBox.width / videoRef.current.videoWidth) * 100}%`, height: `${(faceBoundingBox.height / videoRef.current.videoHeight) * 100}%`, transition: "all 0.2s ease-out" }} />)}
            {isCameraOn && (<span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[8px] font-mono">{facing === "user" ? "FRONT" : "REAR"}</span>)}
          </div>
          {showEsp32 ? (
            <div className="relative aspect-square bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center border border-[var(--bd)]">
              {camImage ? <img src={camImage} alt="ESP32" className="w-full h-full object-contain" /> : (<div className="text-center text-[var(--t2)] p-2"><CamIcon size={20} className="mx-auto mb-1 opacity-40" /><p className="text-[8px] font-mono">ESP32 Camera</p></div>)}
              <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[8px] font-mono flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${camImage ? "bg-[var(--accent)] pulse-dot" : "bg-[var(--t2)]"}`} /> ESP32</span>
              <button onClick={() => { sendCamCommand("snapshot"); toast.info("ESP32 snapshot"); }} className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-[var(--accent)] text-black flex items-center justify-center active:scale-95"><Aperture size={10} /></button>
            </div>
          ) : (
            <button onClick={() => setShowEsp32(true)} className="aspect-square flex flex-col items-center justify-center gap-1 rounded-lg bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] text-[9px] font-mono hover:border-[var(--accent)]"><CamIcon size={18} className="opacity-40" /> Show ESP32</button>
          )}
        </div>
        {/* Row 2: Robot Face */}
        <div className="relative w-full aspect-[4/3] bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center cursor-pointer p-4" onClick={handleWakeUp}>
          <CortexFace animation={animationState} eyePosition={eyePosition} isEyeRolling={isEyeRolling} />
          {transcript && (<p className="absolute bottom-2 left-2 right-2 text-white bg-black/60 backdrop-blur-sm p-1.5 rounded text-[10px] leading-tight">{transcript}</p>)}
        </div>
        {cameraError && (<div className="bg-[var(--danger)]/10 border border-[var(--danger)] rounded-lg p-2 text-[var(--danger)] text-[10px]">{cameraError}</div>)}
        {/* Row 3: Controls */}
        <RoboControlBar isCameraOn={isCameraOn} isListening={micEnabled} facing={facing} toggleCamera={handleToggleCamera} toggleListening={handleMicToggle} flipCamera={flipCamera} endCall={handleEndCall} />
      </div>

      {/* ═══ DESKTOP LAYOUT (md and up) ═══ */}
      <div className="hidden md:flex flex-1 overflow-hidden rounded-lg border border-[var(--bd)]">
        <main className="flex-1 grid grid-cols-2 gap-3 p-3 relative bg-[var(--surface)] rounded-l-lg">
          {/* Left column: Webcam + ESP32 stacked */}
          <div className="flex flex-col gap-3 min-h-0">
            <div className={cn("relative flex-1 min-h-0 bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center border-4 transition-all shadow-lg", videoBorderColor())}>
              <video ref={desktopVideoRef} autoPlay playsInline muted className={`w-full h-full object-cover transition-opacity ${isCameraOn ? "opacity-100" : "opacity-0"}`} style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }} />
              {!isCameraOn && (<div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--t2)] z-10"><CameraOff size={48} /><p className="mt-3 text-xs">Your camera is off</p></div>)}
              {faceBoundingBox && videoRef.current && (<div className="absolute border-2 border-green-500/80 rounded-md shadow-lg" style={{ left: `${(faceBoundingBox.x / videoRef.current.videoWidth) * 100}%`, top: `${(faceBoundingBox.y / videoRef.current.videoHeight) * 100}%`, width: `${(faceBoundingBox.width / videoRef.current.videoWidth) * 100}%`, height: `${(faceBoundingBox.height / videoRef.current.videoHeight) * 100}%`, transition: "all 0.2s ease-out" }} />)}
              {isCameraOn && (<span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono">{facing === "user" ? "FRONT" : "REAR"}</span>)}
            </div>
            {showEsp32 && (
              <div className="relative flex-1 min-h-0 bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center border border-[var(--bd)]">
                {camImage ? <img src={camImage} alt="ESP32 camera" className="w-full h-full object-contain" /> : (<div className="text-center text-[var(--t2)] p-4"><CamIcon size={32} className="mx-auto mb-2 opacity-40" /><p className="text-[10px] font-mono">ESP32 Camera</p><p className="text-[9px] mt-0.5 opacity-60">No feed yet</p></div>)}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${camImage ? "bg-[var(--accent)] pulse-dot" : "bg-[var(--t2)]"}`} /> ESP32</span>
                <div className="absolute bottom-2 right-2 flex gap-1.5">
                  <button onClick={() => { sendCamCommand("snapshot"); toast.info("Snapshot requested from ESP32"); }} className="w-8 h-8 rounded-full bg-[var(--accent)] text-black flex items-center justify-center active:scale-95 transition-transform"><Aperture size={14} /></button>
                  <button onClick={() => setShowEsp32(false)} className="w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-95 transition-transform border border-white/20"><X size={14} /></button>
                </div>
              </div>
            )}
            {!showEsp32 && (<button onClick={() => setShowEsp32(true)} className="flex items-center justify-center gap-2 py-2 rounded-lg bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] text-xs font-mono hover:border-[var(--accent)] transition-colors"><CamIcon size={12} /> Show ESP32 Camera</button>)}
          </div>

          {/* Right column: Robot Face */}
          <div className="relative w-full h-full min-h-0 bg-[var(--surface2)] rounded-lg overflow-hidden flex items-center justify-center cursor-pointer" onClick={handleWakeUp}>
            <CortexFace animation={animationState} eyePosition={eyePosition} isEyeRolling={isEyeRolling} />
            {transcript && (<p className="absolute bottom-4 left-4 right-4 text-[var(--t1)] bg-[var(--surface)]/80 backdrop-blur-sm p-2 rounded border border-[var(--bd)] text-sm">{transcript}</p>)}
          </div>

          {cameraError && (<div className="absolute bottom-14 left-4 right-4 w-auto max-w-md mx-auto z-30 bg-[var(--danger)]/10 border border-[var(--danger)] rounded-lg p-4 text-[var(--danger)] text-sm">{cameraError}</div>)}
        </main>

        <RoboSidePanel
          isOpen={isSidePanelOpen}
          onClose={() => setIsSidePanelOpen(false)}
          isGenerating={isGenerating}
          isThinking={isThinking}
          storyContent={storyContent}
          onConversation={handleConversation}
          conversationHistory={conversationHistory}
        />
      </div>
      <div className="hidden md:block">
        <RoboControlBar isCameraOn={isCameraOn} isListening={micEnabled} facing={facing} toggleCamera={handleToggleCamera} toggleListening={handleMicToggle} flipCamera={flipCamera} endCall={handleEndCall} />
      </div>
    </div>
  );
}
